import React, { useMemo, useState } from 'react'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useSpeechStore } from '../Contexts/SpeechContext'
import { useActivityStore } from '../Contexts/ActivityContext'
import { useUtilsStore } from '../Contexts/UtilsContext'
import { useViewStore } from '../Contexts/ViewContext'

import { DeviceType, Displays, ListStatus } from '../../models/enums'
import { RetorikActivity } from '../../models/activityTypes'

import ListTemplate from '../Templates/List'
import Attachments from '../Attachments/Attachments'

const BotResponse = (): JSX.Element => {
  const speaking = useSpeechStore((state) => state.speaking)
  const lastRecognitionInterim = useSpeechStore((state) => state.lastRecognitionInterim)
  const currentOrLastPlayedActivity = useSpeechStore((state) => state.currentOrLastPlayedActivity)
  const botMessageActivities = useActivityStore((state) => state.botMessageActivities)
  const currentDeviceType = useViewStore((state) => state.currentDeviceType)
  const currentWidth = useViewStore((state) => state.currentWidth)
  const currentHeight = useViewStore((state) => state.currentHeight)
  const mediumLandscape = useViewStore((state) => state.mediumLandscape)
  const configuration = useRetorikStore((state) => state.configuration)
  const displaySubtitles = useRetorikStore((state) => state.displaySubtitles)
  const listStatus = useUtilsStore((state) => state.listStatus)
  const setListStatus = useUtilsStore((state) => state.actions.setListStatus)

  const [gridRowStart, setGridRowStart] = useState<number>()
  const [gridRowSpan, setGridRowSpan] = useState<number>()

  const botResponseClassname = useMemo<string>(() => {
    let classname = 'rf-relative rf-w-full rf-h-full rf-z-util rf-overflow-y-scroll rf-scrollbar-thin'

    switch (currentDeviceType) {
      case DeviceType.mobile:
      case DeviceType.widget: {
        // Special case for very small screens
        const isSmall = currentHeight < 700 && currentWidth < 300
        classname += ' rf-col-start-1 rf-col-span-full'
        setGridRowStart(isSmall ? 5 : 6)
        if (displaySubtitles && speaking) {
          setGridRowSpan(isSmall ? 6 : 5)
        } else {
          setGridRowSpan(isSmall ? 7 : 6)
        }
        break
      }
      case DeviceType.landscape:
      case DeviceType.widgetLandscape: {
        classname += ` ${mediumLandscape ? 'rf-col-start-3' : 'rf-col-start-4'} rf-col-span-full`

        if (typeof configuration.answerpanel === 'object' && configuration.answerpanel.landscape) {
          const start = configuration.answerpanel.landscape.containerRowStart
          const end = configuration.answerpanel.landscape.containerRowEnd

          if (start && start > 0 && start < 13) {
            if (end && end < 13) {
              if (start < end) {
                setGridRowStart(start)
                setGridRowSpan(end - start)
              }
            } else {
              setGridRowStart(start)
              setGridRowSpan(13 - start)
            }
          } else if (end && end > 0 && end < 13) {
            setGridRowStart(1)
            setGridRowSpan(end - 1)
          } else {
            setGridRowStart(4)
            setGridRowSpan(9)
          }
        } else {
          setGridRowStart(4)
          setGridRowSpan(9)
        }
        break
      }
      case DeviceType.borne:
      case DeviceType.widgetBorne: {
        classname += ' rf-col-start-1 rf-col-span-full'

        if (typeof configuration.answerpanel === 'object' && configuration.answerpanel.borne) {
          const start = configuration.answerpanel.borne.containerRowStart
          const end = configuration.answerpanel.borne.containerRowEnd

          if (start && start > 0 && start < 13) {
            if (end && end < 13) {
              if (start < end) {
                setGridRowStart(start)
                setGridRowSpan(end - start)
              }
            } else {
              setGridRowStart(start)
              setGridRowSpan(13 - start)
            }
          } else if (end && end > 0 && end < 13) {
            setGridRowStart(1)
            setGridRowSpan(end - 1)
          } else {
            setGridRowStart(6)
            setGridRowSpan(5)
          }
        } else {
          setGridRowStart(6)
          setGridRowSpan(5)
        }
        break
      }
    }

    return classname
  }, [currentDeviceType, displaySubtitles, speaking, configuration.answerpanel, currentWidth, currentHeight])

  /**
   * Check if the current activity is a streaming one without attachments.
   * If so, let's check if we already received the message at the end of the streaming asn if it contains attachments to be displayed
   */
  const processedCurrentOrLastPlayedActivity = useMemo<RetorikActivity | undefined>(() => {
    if (currentOrLastPlayedActivity?.name?.toLowerCase() === 'davi.streammessageactivity' && !currentOrLastPlayedActivity?.attachments?.length) {
      const relatedMessageActivityWithAttachments = botMessageActivities.find(
        (activity) => activity.value?.streamingCount && activity.replyToId === currentOrLastPlayedActivity.replyToId && activity.attachments?.length
      )
      if (relatedMessageActivityWithAttachments) {
        return relatedMessageActivityWithAttachments
      }
    }

    return currentOrLastPlayedActivity
  }, [currentOrLastPlayedActivity, botMessageActivities])

  const currentDisplay = useMemo<Displays>(() => {
    if (processedCurrentOrLastPlayedActivity) {
      if (processedCurrentOrLastPlayedActivity.attachmentLayout) {
        switch (processedCurrentOrLastPlayedActivity.attachmentLayout.toLowerCase()) {
          case 'davilist':
            return Displays.list
          case 'carousel':
          case 'namedlist':
            return processedCurrentOrLastPlayedActivity.attachments && processedCurrentOrLastPlayedActivity.attachments.length > 1 ? Displays.carousel : Displays.normal
          case 'grid':
            return Displays.grid
        }
      }

      // If we have no attachment layout but attachment(s), we use list display by default is there are several attachments
      if (processedCurrentOrLastPlayedActivity.attachments?.length) {
        return processedCurrentOrLastPlayedActivity.attachments?.length > 1 ? Displays.list : Displays.normal
      }
    }

    return Displays.none
  }, [processedCurrentOrLastPlayedActivity])

  return currentDisplay === Displays.none ? (
    <React.Fragment />
  ) : currentDisplay === Displays.list ? (
    lastRecognitionInterim === '' && !(listStatus === ListStatus.closed) ? (
      <ListTemplate activity={processedCurrentOrLastPlayedActivity} onClose={(): void => setListStatus(ListStatus.closed)} />
    ) : (
      <React.Fragment />
    )
  ) : (
    <div
      id='retorik-botResponse'
      className={botResponseClassname}
      style={{
        gridRow: `${gridRowStart} / span ${gridRowSpan}`
      }}
    >
      {processedCurrentOrLastPlayedActivity && (
        <div className='rf-w-full rf-h-full rf-flex rf-flex-col landscape-retorik:rf-items-start vertical:rf-items-center rf-gap-2'>
          <Attachments activity={processedCurrentOrLastPlayedActivity} />
        </div>
      )}
    </div>
  )
}

export default BotResponse
